/**
 * TODO:
 * - Please move this logic to the Alpine store.
 * - Please remove the "allow-scroll" references in the code. It is used to "whitelist" scrollable elements when body scrolling is locked.
 * Ticket: https://tubeengine.atlassian.net/browse/SB-4009
 */

// Define interface for storing original styles
interface OriginalStyles {
    overflow: string;
    position: string;
    height: string;
    width: string;
    top: string;
    left: string;
}

// Store original body style to restore later
let originalStyles: OriginalStyles = {
    overflow: '',
    position: '',
    height: '',
    width: '',
    top: '',
    left: '',
};

// Store the initial scroll position
let scrollPosition = 0;

/**
 * Disables scrolling on the page body
 */
export const disableBodyScroll = () => {
    // Store current scroll position
    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Store original styles before modifying
    const properties = ['overflow', 'position', 'height', 'width', 'top', 'left'];
    properties.forEach((property) => {
        originalStyles[property as keyof OriginalStyles] = document.body.style[property as any];
    });

    const { style: originalBodyStyle } = document.body;
    originalStyles = {
        overflow: originalBodyStyle.overflow,
        position: originalBodyStyle.position,
        height: originalBodyStyle.height,
        width: originalBodyStyle.width,
        top: originalBodyStyle.top,
        left: originalBodyStyle.left,
    };

    // Apply styles to prevent scrolling
    const { style: bodyStyle } = document.body;
    bodyStyle.overflow = 'hidden';
    bodyStyle.position = 'fixed';
    bodyStyle.height = '100%';
    bodyStyle.width = '100%';
    bodyStyle.top = `-${scrollPosition}px`;
    bodyStyle.left = '0';

    // Also apply to html for better cross-browser support
    document.documentElement.style.overflow = 'hidden';

    // Prevent touchmove events for iOS Safari
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
};

/**
 * Re-enables scrolling on the page body
 */
export const enableBodyScroll = () => {
    // Remove touchmove event listener
    document.removeEventListener('touchmove', preventTouchMove);

    // Restore original styles to body
    const { style: bodyStyle } = document.body;
    bodyStyle.overflow = originalStyles.overflow;
    bodyStyle.position = originalStyles.position;
    bodyStyle.height = originalStyles.height;
    bodyStyle.width = originalStyles.width;
    bodyStyle.top = originalStyles.top;
    bodyStyle.left = originalStyles.left;

    // Restore html style
    document.documentElement.style.overflow = '';

    // Restore scroll position
    window.scrollTo(0, scrollPosition);
};

/**
 * Helper to find scrollable parent elements
 * This allows scrolling within specific containers even when page scroll is disabled
 * @param element - The element to check
 * @return The scrollable parent or null if none found
 */
const findScrollableParent = (element: Element | null): Element | null => {
    // Add logic here if you want to allow scrolling in specific containers
    // For example, elements with a class 'allow-scroll'
    while (element && element !== document.body) {
        if (element.classList.contains('allow-scroll')) {
            return element;
        }
        element = element.parentElement;
    }
    return null;
};

/**
 * Helper to prevent touch move events
 * Particularly important for iOS Safari
 */
const preventTouchMove = (e: TouchEvent) => {
    // Check if scrolling should be allowed in specific elements (optional)
    const allowedElement = findScrollableParent(e.target as Element);

    if (!allowedElement) {
        e.preventDefault();
    }
};
